import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  getMyZones,
  createZone,
  deleteZone,
  renewZone,
  getZonePrice,
  ZonePriceResult,
  getZonesInArea,
} from '../../api/zones';
import { Zone, isZoneActive } from '../../types/zone';
import { WorldCanvas, Viewport } from '../WorldCanvas/WorldCanvas';
import { ZoneOverlay } from '../ZoneOverlay/ZoneOverlay';
import { ZoneDrawOverlay } from '../ZoneOverlay/ZoneDrawOverlay';
import { FreeBoundaryOverlay } from '../ZoneOverlay/FreeBoundaryOverlay';
import { WebSocketClient, WsEvent, getWsBase } from '../../api/websocket';
import { Point } from '../../types/point';
import { toastError, toastSuccess } from '../../utils/toast';
import { trackEvent } from '../../utils/analytics';

// ─── Map overlay (fullscreen, guest WS) ──────────────────────────────────────

interface MapOverlayProps {
  onSelect: (x1: number, y1: number, x2: number, y2: number) => void;
  onCancel: () => void;
}

const MapOverlay: React.FC<MapOverlayProps> = ({ onSelect, onCancel }) => {
  const { t } = useTranslation();

  const [viewport,   setViewport]   = useState<Viewport>({ x: 0, y: 0, zoom: 1 });
  const [points,     setPoints]     = useState<Point[]>([]);
  const [zones,      setZones]      = useState<Zone[]>([]);
  const [drawMode,   setDrawMode]   = useState(false);
  const [coordInput, setCoordInput] = useState('');
  const [winSize,    setWinSize]    = useState({ width: window.innerWidth, height: window.innerHeight });

  const vpRef      = useRef(viewport);
  vpRef.current    = viewport;
  const winRef     = useRef(winSize);
  winRef.current   = winSize;
  const wsRef      = useRef<WebSocketClient | null>(null);
  const resubTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const zoneTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !drawMode) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawMode, onCancel]);

  useEffect(() => {
    const onResize = () => setWinSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const sendSubscribe = useCallback((vp: Viewport) => {
    const { width: w, height: h } = winRef.current;
    const halfW = Math.ceil(w / 2 / vp.zoom) + 10;
    const halfH = Math.ceil(h / 2 / vp.zoom) + 10;
    wsRef.current?.send({
      type: 'subscribe',
      area: {
        x_min: Math.round(vp.x) - halfW, y_min: Math.round(vp.y) - halfH,
        x_max: Math.round(vp.x) + halfW, y_max: Math.round(vp.y) + halfH,
      },
    });
  }, []);

  const fetchZones = useCallback((vp: Viewport) => {
    const { width: w, height: h } = winRef.current;
    const halfW = Math.ceil(w / 2 / vp.zoom) + 20;
    const halfH = Math.ceil(h / 2 / vp.zoom) + 20;
    getZonesInArea(
      Math.round(vp.x) - halfW, Math.round(vp.y) - halfH,
      Math.round(vp.x) + halfW, Math.round(vp.y) + halfH,
    ).then(res => setZones(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const vp = vpRef.current;
    const { width: w, height: h } = winRef.current;
    const halfW = Math.ceil(w / 2 / vp.zoom) + 10;
    const halfH = Math.ceil(h / 2 / vp.zoom) + 10;
    const url = `${getWsBase()}?token=guest&area=${Math.round(vp.x)-halfW},${Math.round(vp.y)-halfH},${Math.round(vp.x)+halfW},${Math.round(vp.y)+halfH}`;

    const client = new WebSocketClient(url, (event: WsEvent) => {
      if (event.type === 'init' || event.type === 'points_update') {
        setPoints(Array.isArray(event.points) ? event.points : []);
      } else if (event.type === 'moved') {
        setPoints(prev => prev.map(p => p.id === event.pointId ? { ...p, x: event.newX, y: event.newY } : p));
      }
    });
    wsRef.current = client;
    fetchZones(vpRef.current);
    return () => {
      client.close();
      wsRef.current = null;
      if (resubTimer.current) clearTimeout(resubTimer.current);
      if (zoneTimer.current)  clearTimeout(zoneTimer.current);
    };
  }, [fetchZones]);

  const handleVpChange = useCallback((vp: Viewport) => {
    setViewport(vp);
    if (resubTimer.current) clearTimeout(resubTimer.current);
    resubTimer.current = setTimeout(() => sendSubscribe(vp), 250);
    if (zoneTimer.current) clearTimeout(zoneTimer.current);
    zoneTimer.current = setTimeout(() => fetchZones(vp), 500);
  }, [sendSubscribe, fetchZones]);

  const handleGoTo = () => {
    const parts = coordInput.split(',').map(s => parseInt(s.trim(), 10));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      handleVpChange({ ...vpRef.current, x: parts[0], y: parts[1] });
      setCoordInput('');
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: '#000' }}>
      <WorldCanvas points={points} viewport={viewport} width={winSize.width} height={winSize.height} onViewportChange={handleVpChange} />
      <FreeBoundaryOverlay viewport={viewport} width={winSize.width} height={winSize.height} />
      <ZoneOverlay zones={zones} viewport={viewport} width={winSize.width} height={winSize.height} />
      {drawMode && (
        <ZoneDrawOverlay
          viewport={viewport} width={winSize.width} height={winSize.height}
          onSelect={(x1, y1, x2, y2) => { setDrawMode(false); onSelect(x1, y1, x2, y2); }}
          onCancel={() => setDrawMode(false)}
        />
      )}
      {!drawMode && (
        <div style={{
          position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10, display: 'flex', gap: '6px', alignItems: 'center',
          background: 'rgba(0,0,0,0.75)', padding: '6px 12px', borderRadius: '4px',
        }}>
          <span style={{ fontSize: '11px', opacity: 0.6, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
            X:{Math.round(viewport.x)} Y:{Math.round(viewport.y)}
          </span>
          <input
            style={{
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
              color: '#fff', fontFamily: 'monospace', fontSize: '11px', padding: '2px 6px', width: '90px',
            }}
            placeholder="x, y"
            value={coordInput}
            onChange={e => setCoordInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleGoTo()}
          />
          <button className="btn-transparent" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={handleGoTo}>GO</button>
          <button className="btn-transparent" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => setDrawMode(true)}>
            {t('zone.drawBtn')}
          </button>
          <button className="btn-transparent" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={onCancel}>✕</button>
        </div>
      )}
    </div>
  );
};

// ─── ZonesSection ─────────────────────────────────────────────────────────────

export const ZonesSection: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [zones,   setZones]   = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);

  const [deleteTarget, setDeleteTarget] = useState<Zone | null>(null);
  const [deleting,     setDeleting]     = useState(false);
  const [renewingId,   setRenewingId]   = useState<number | null>(null);

  const [showForm,   setShowForm]   = useState(false);
  const [showMap,    setShowMap]    = useState(false);
  const [fName,      setFName]      = useState('');
  const [fX1,        setFX1]        = useState('');
  const [fY1,        setFY1]        = useState('');
  const [fX2,        setFX2]        = useState('');
  const [fY2,        setFY2]        = useState('');
  const [fTimeout,   setFTimeout]   = useState('30');
  const [fPrice,     setFPrice]     = useState<ZonePriceResult | null>(null);
  const [fPriceLoad, setFPriceLoad] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [formErr,    setFormErr]    = useState('');
  const [highlightZoneId, setHighlightZoneId] = useState<number | null>(null);
  const priceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrolledZoneRef = useRef<number | null>(null);
  const returnPath = '/dashboard?tab=zones';

  const load = useCallback(() => {
    setLoading(true);
    getMyZones()
      .then(res => setZones(res.data))
      .catch(() => toastError(t('zone.errCreate')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const zoneId = Number(searchParams.get('zone_id') ?? '');
    if (!Number.isFinite(zoneId) || zoneId <= 0 || scrolledZoneRef.current === zoneId) return;
    const targetExists = zones.some(z => z.id === zoneId);
    if (!targetExists) return;

    scrolledZoneRef.current = zoneId;
    setHighlightZoneId(zoneId);

    const timer = window.setTimeout(() => {
      document.getElementById(`zone-card-${zoneId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    const clearTimer = window.setTimeout(() => setHighlightZoneId(current => current === zoneId ? null : current), 2500);

    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clearTimer);
    };
  }, [searchParams, zones]);

  useEffect(() => {
    if (!showForm) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowForm(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showForm]);

  useEffect(() => {
    if (!showForm) return;
    const x1 = parseInt(fX1, 10); const y1 = parseInt(fY1, 10);
    const x2 = parseInt(fX2, 10); const y2 = parseInt(fY2, 10);
    if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) { setFPrice(null); return; }
    if (priceTimer.current) clearTimeout(priceTimer.current);
    setFPriceLoad(true);
    priceTimer.current = setTimeout(() => {
      getZonePrice(x1, y1, x2, y2)
        .then(res => setFPrice(res.data))
        .catch(() => setFPrice(null))
        .finally(() => setFPriceLoad(false));
    }, 400);
    return () => { if (priceTimer.current) clearTimeout(priceTimer.current); };
  }, [showForm, fX1, fY1, fX2, fY2]);

  const openForm = () => {
    setFName(''); setFX1(''); setFY1(''); setFX2(''); setFY2('');
    setFTimeout('30'); setFPrice(null); setFormErr('');
    setShowForm(true);
  };

  const handleCreate = async () => {
    const x1 = parseInt(fX1, 10); const y1 = parseInt(fY1, 10);
    const x2 = parseInt(fX2, 10); const y2 = parseInt(fY2, 10);
    const timeout = parseInt(fTimeout, 10);
    if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) {
      setFormErr(t('zone.errInvalidCoords'));
      return;
    }
    setSaving(true); setFormErr('');
    const width  = Math.abs(x2 - x1) + 1;
    const height = Math.abs(y2 - y1) + 1;
    const isFree = fPrice?.is_free === true && freeAvail;
    trackEvent('zone_create_initiate', {
      area  : width * height,
      width,
      height,
      is_free: isFree,
      amount_rub: isFree ? 0 : (fPrice?.amount_rub ?? null),
    });
    try {
      const res = await createZone(
        fName.trim(),
        Math.min(x1, x2), Math.min(y1, y2),
        Math.max(x1, x2), Math.max(y1, y2),
        isNaN(timeout) || timeout <= 0 ? 30 : timeout,
        returnPath,
      );
      const { payment_url } = res.data;
      if (payment_url) {
        window.location.href = payment_url;
      } else {
        trackEvent('zone_create_success', { kind: 'free' });
        setShowForm(false);
        toastSuccess(t('zone.created'));
        load();
      }
    } catch (e: any) {
      trackEvent('zone_create_error', { reason: String(e?.response?.data?.error || e?.message || 'unknown').slice(0, 64) });
      setFormErr(e?.response?.data?.error || t('zone.errCreate'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteZone(deleteTarget.id);
      trackEvent('zone_delete', { zone_id: deleteTarget.id, is_free: deleteTarget.is_free });
      setZones(prev => prev.filter(x => x.id !== deleteTarget.id));
      toastSuccess(t('zone.deleted'));
      setDeleteTarget(null);
    } catch (e: any) {
      toastError(e?.response?.data?.error || t('zone.errDelete'));
    } finally {
      setDeleting(false);
    }
  };

  const handleRenew = async (z: Zone) => {
    setRenewingId(z.id);
    trackEvent('zone_renew_initiate', { zone_id: z.id });
    try {
      const res = await renewZone(z.id, returnPath);
      window.location.href = res.data.payment_url;
    } catch (e: any) {
      toastError(e?.response?.data?.error || t('zone.errRenew'));
      setRenewingId(null);
    }
  };

  const freeUsed  = zones.filter(z => z.is_free).length;
  const freeAvail = freeUsed === 0;

  const daysUntilExpiry = (z: Zone): number | null => {
    if (!z.expires_at) return null;
    return Math.ceil((new Date(z.expires_at).getTime() - Date.now()) / 86_400_000);
  };
  const EXPIRY_WARN_DAYS = 3;
  const expiringSoon = zones.filter(z => !z.is_free && (daysUntilExpiry(z) ?? 99) <= EXPIRY_WARN_DAYS && (daysUntilExpiry(z) ?? 99) > 0);

  const statusBadge = (z: Zone) => {
    if (z.is_free)      return <span className="zone-badge zone-badge--free">{t('myZones.statusFree')}</span>;
    if (isZoneActive(z)) return <span className="zone-badge zone-badge--active">{t('myZones.statusActive')}</span>;
    if (z.expires_at)   return <span className="zone-badge zone-badge--expired">{t('myZones.statusExpired')}</span>;
    return <span className="zone-badge zone-badge--pending">{t('myZones.statusPending')}</span>;
  };

  return (
    <>
      {/* Expiring-soon banner */}
      {expiringSoon.length > 0 && (
        <div style={{ background: 'rgba(255,140,0,0.15)', border: '1px solid rgba(255,140,0,0.4)', borderRadius: '4px', padding: '8px 14px', margin: '0.75rem 0', fontSize: '12px', color: '#ffaa44' }}>
          {t('zone.expiringSoonBanner', { count: expiringSoon.length })}
        </div>
      )}

      {/* Stats */}
      <div className="dashboard__stats">
        <div className="stat-chip">
          {t('myZones.statFree')} <span className="stat-chip__value" style={{ color: freeAvail ? '#88ff88' : '#ff8888' }}>
            {freeUsed} / 1
          </span>
        </div>
        <div className="stat-chip">
          {t('myZones.statTotal')} <span className="stat-chip__value">{zones.length}</span>
        </div>
      </div>

      {/* Zone list */}
      {loading ? (
        <div className="dashboard__loading">{t('common.loading')}</div>
      ) : zones.length === 0 ? (
        <div className="dashboard__empty">
          <div className="dashboard__empty-icon">[⬛]</div>
          <p className="dashboard__empty-text">{t('myZones.empty')}</p>
        </div>
      ) : (
        <div className="zones-list">
          {zones.map(z => {
            const days = daysUntilExpiry(z);
            const expiringSoonCard = !z.is_free && days !== null && days <= EXPIRY_WARN_DAYS && days > 0;
            return (
              <div
                key={z.id}
                id={`zone-card-${z.id}`}
                className={`zone-card${isZoneActive(z) ? '' : ' zone-card--inactive'}`}
                style={highlightZoneId === z.id ? {
                  borderColor: 'rgba(136,255,136,0.85)',
                  boxShadow: '0 0 0 1px rgba(136,255,136,0.45), 0 0 22px rgba(136,255,136,0.2)',
                } : undefined}
              >
                <div className="zone-card__header">
                  <span className="zone-card__name">{z.name || `#${z.id}`}</span>
                  {statusBadge(z)}
                </div>
                <div className="zone-card__coords">
                  ({z.x_min}, {z.y_min}) – ({z.x_max}, {z.y_max})
                  <span className="zone-card__size">{z.x_max - z.x_min + 1} × {z.y_max - z.y_min + 1}</span>
                </div>
                <div className="zone-card__meta">
                  <span>{t('zone.timeoutLabel')} {z.eviction_timeout_sec}{t('zone.seconds')}</span>
                  {!z.is_free && z.expires_at && (
                    <span style={expiringSoonCard ? { color: '#ffaa44' } : {}}>
                      {t('zone.expiresAt', { date: new Date(z.expires_at).toLocaleDateString() })}
                      {expiringSoonCard && ` — ${days}д.`}
                    </span>
                  )}
                  {!z.is_free && !z.expires_at && (
                    <span style={{ color: '#ffbb44' }}>{t('zone.pendingPayment')}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '4px', position: 'absolute', top: '8px', right: '8px' }}>
                  {!z.is_free && isZoneActive(z) && (
                    <button
                      className="zone-card__delete"
                      style={{ background: expiringSoonCard ? 'rgba(255,140,0,0.3)' : undefined }}
                      onClick={() => handleRenew(z)}
                      disabled={renewingId === z.id}
                      title={t('zone.renewBtn')}
                    >
                      {renewingId === z.id ? '…' : '↻'}
                    </button>
                  )}
                  <button className="zone-card__delete" onClick={() => setDeleteTarget(z)}>✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create button */}
      <div style={{ marginTop: '2rem' }}>
        <button className="dashboard__page-btn" onClick={openForm} style={{ padding: '10px 24px', fontSize: '13px' }}>
          + {t('myZones.createBtn')}
        </button>
      </div>

      {/* Create form modal */}
      {showForm && (
        <div className="pixel-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="pixel-modal" style={{ minWidth: '300px', maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <p className="pixel-modal__title">{t('zone.reserveTitle')}</p>
            <input
              className="pixel-modal__input"
              placeholder={t('zone.namePlaceholder')}
              value={fName}
              onChange={e => setFName(e.target.value)}
              autoFocus
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 4px' }}>
              <span style={{ fontSize: '11px', opacity: 0.6 }}>{t('myZones.coordsLabel')}</span>
              <button
                className="pixel-modal__btn"
                style={{ padding: '3px 10px', fontSize: '11px' }}
                onClick={() => { setShowForm(false); setShowMap(true); }}
              >
                {t('zone.drawBtn')}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              <input className="pixel-modal__input" placeholder="X min" value={fX1} onChange={e => setFX1(e.target.value)} />
              <input className="pixel-modal__input" placeholder="Y min" value={fY1} onChange={e => setFY1(e.target.value)} />
              <input className="pixel-modal__input" placeholder="X max" value={fX2} onChange={e => setFX2(e.target.value)} />
              <input className="pixel-modal__input" placeholder="Y max" value={fY2} onChange={e => setFY2(e.target.value)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0 4px' }}>
              <span style={{ fontSize: '11px', opacity: 0.6 }}>{t('zone.timeoutLabel')}</span>
              <input
                className="pixel-modal__input"
                style={{ width: '70px' }}
                type="number" min="5"
                value={fTimeout}
                onChange={e => setFTimeout(e.target.value)}
              />
              <span style={{ fontSize: '11px', opacity: 0.6 }}>{t('zone.seconds')}</span>
            </div>
            {(() => {
              const is2ndFrontier = fPrice?.is_free && !freeAvail;
              const frontierAmount = is2ndFrontier && fPrice ? Math.max(1, Math.ceil(fPrice.area * 0.005)) : 0;
              const effectiveAmount = is2ndFrontier ? frontierAmount : (fPrice?.amount_rub ?? 0);
              return (
                <>
                  <div style={{ fontSize: '12px', margin: '8px 0 4px', minHeight: '20px' }}>
                    <span style={{ opacity: 0.55 }}>{t('zone.priceLabel')} </span>
                    {fPriceLoad
                      ? <span style={{ opacity: 0.6 }}>{t('zone.priceCalculating')}</span>
                      : fPrice
                        ? fPrice.is_free && freeAvail
                          ? <strong style={{ color: '#88ff88' }}>✓ {t('zone.priceFree')}</strong>
                          : <strong style={{ color: '#ffcc44' }}>{t('zone.priceAmount', { amount: effectiveAmount })}</strong>
                        : null}
                  </div>
                  <div style={{ fontSize: '10px', opacity: 0.45, marginBottom: '4px' }}>
                    {is2ndFrontier ? t('zone.freeHint2') : t('zone.freeHint')}
                  </div>
                  {fPrice?.has_overlap && (
                    <div style={{ fontSize: '11px', color: '#ff6666', marginBottom: '6px' }}>
                      {t('zone.overlapWarning')}
                    </div>
                  )}
                </>
              );
            })()}
            {formErr && <div style={{ fontSize: '12px', color: '#ff6666', margin: '4px 0' }}>{formErr}</div>}
            <p style={{ fontSize: '10px', opacity: 0.55, margin: '4px 0 6px', textAlign: 'center' }}>
              {t('common.ofertaConsent')}{' '}
              <Link to="/about?tab=oferta" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--px-accent)', textDecoration: 'underline' }}>
                {t('common.ofertaLink')}
              </Link>
            </p>
            <div className="pixel-modal__actions">
              <button className="pixel-modal__btn" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
              <button
                className="pixel-modal__btn pixel-modal__btn--primary"
                onClick={handleCreate}
                disabled={saving || fPrice?.has_overlap === true}
              >
                {saving
                  ? t('common.saving')
                  : fPrice && (!fPrice.is_free || !freeAvail)
                    ? t('myZones.payBtn', { amount: fPrice.is_free && !freeAvail ? Math.max(1, Math.ceil(fPrice.area * 0.005)) : fPrice.amount_rub })
                    : t('zone.createBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map overlay */}
      {showMap && (
        <MapOverlay
          onSelect={(x1, y1, x2, y2) => {
            setFX1(String(x1)); setFY1(String(y1));
            setFX2(String(x2)); setFY2(String(y2));
            setShowMap(false);
            setShowForm(true);
          }}
          onCancel={() => { setShowMap(false); setShowForm(true); }}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="pixel-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="pixel-modal" style={{ maxWidth: '360px' }} onClick={e => e.stopPropagation()}>
            <p className="pixel-modal__title">{t('zone.deleteConfirmTitle')}</p>
            <p style={{ fontSize: '13px', marginBottom: '1rem', opacity: 0.85 }}>
              {t('zone.deleteConfirmText', { name: deleteTarget.name || `#${deleteTarget.id}` })}
            </p>
            {deleteTarget.expires_at && !deleteTarget.is_free && (
              <p style={{ fontSize: '11px', color: '#ffaa44', marginBottom: '0.75rem' }}>
                ⚠ {t('zone.deleteNoRefund')}
              </p>
            )}
            <div className="pixel-modal__actions">
              <button className="pixel-modal__btn" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                {t('common.cancel')}
              </button>
              <button
                className="pixel-modal__btn pixel-modal__btn--primary"
                style={{ background: 'rgba(200,40,40,0.7)' }}
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? '...' : t('zone.deleteConfirmBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
